
import { Card, Table, Button, Badge, Spinner, Modal, Form, Row, Col, Alert } from "react-bootstrap";
import { FileEarmark, Gear } from "react-bootstrap-icons";
import { formatCurrency } from "../../../store/config";
import ContentWrapper from "../../utilities/ContentWrapper";
import { useLandAcquisitionRequests } from "../../hooks/useLandAquisitionRequest";
import { useLandAcquisitionActions } from "../../hooks/useLandAquisitionActions";
import { useSystemConfigActions } from "../../hooks/useSystemConfigActions";
import CommentsModal from "../withdrawal/CommentModal";
import StatusBadge from "../withdrawal/StatusBadge";
import LandAcquisitionFilters from "./LandAquisitionFilters";
import LandAcquisitionDetailsModal from "./LandAquisitionModal";
import PaginationComponent from "../withdrawal/PaginationComponent";
import Swal from "sweetalert2";

function LandAcquisitionManagement() {
    const {
        acquisitionRequests,
        systemConfig,
        isLoading,
        pagination,
        filterStatus,
        filterMemberType,
        searchQuery,
        currentPage,
        setSearchQuery,
        handleFilterChange,
        handleMemberTypeChange,
        handleSearch,
        handlePageChange,
        resetFilters,
        refreshRequests
    } = useLandAcquisitionRequests();

    const {
        selectedRequest,
        showDetailsModal,
        showCommentsModal,
        processingAction,
        comments,
        setComments,
        setShowCommentsModal,
        handleViewDetails,
        handleUpdateStatus,
        closeModals
    } = useLandAcquisitionActions();

    const {
        showConfigModal,
        setShowConfigModal,
        configFormData,
        setConfigFormData,
        configError,
        isSavingConfig,
        handleConfigInputChange,
        handleStatusChange,
        handleSaveConfig,
        handleCancel
    } = useSystemConfigActions(systemConfig, refreshRequests);

    const statusBadgeElement = (status) => <StatusBadge status={status} />;

    const memberTypeBadge = (memberType) => {
        return (
            <Badge variant={memberType === 'member' ? 'primary' : 'secondary'}>
                {memberType === 'member' ? 'Member' : 'Non-Member'}
            </Badge>
        );
    };

    const openConfigModal = () => {
        if (systemConfig) {
            setConfigFormData({
                plotPrice: systemConfig.plotPrice,
                nonMemberPlotPrice: systemConfig.nonMemberPlotPrice,
                minimumPercentage: systemConfig.minimumPercentage,
                maximumInstallments: systemConfig.maximumInstallments,
                availablePlots: {
                    blossom: { ...systemConfig.availablePlots.blossom },
                    university: { ...systemConfig.availablePlots.university }
                }
            });
            setShowConfigModal(true);
        } else {
            Swal.fire({
                icon: 'error',
                title: 'Configuration not loaded',
                text: 'System configuration data is not available yet'
            });
        }
    };

    return (
        <ContentWrapper>
            {/* Header with config button */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">Land Acquisition Management</h4>
                <Button
                    variant="outline-primary"
                    className="d-flex align-items-center"
                    onClick={openConfigModal}
                >
                    <Gear className="mr-2" />
                    System Configuration
                </Button>
            </div>

            {/* Configuration Summary Card */}
            {systemConfig && (
                <Card className="mb-4 shadow-sm">
                    <Card.Header className="bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Current Configuration</h5>
                            <Button
                                variant="link"
                                className="p-0"
                                onClick={openConfigModal}
                            >
                                Edit
                            </Button>
                        </div>
                    </Card.Header>
                    <Card.Body className="py-2">
                        <Row>
                            <Col md={3} className="border-right">
                                <div className="text-center py-2">
                                    <div className="text-muted small">Member Price</div>
                                    <div className="h5">{formatCurrency(systemConfig?.plotPrice)}</div>
                                </div>
                            </Col>
                            <Col md={3} className="border-right">
                                <div className="text-center py-2">
                                    <div className="text-muted small">Non-Member Price</div>
                                    <div className="h5">{formatCurrency(systemConfig?.nonMemberPlotPrice)}</div>
                                </div>
                            </Col>
                            <Col md={3} className="border-right">
                                <div className="text-center py-2">
                                    <div className="text-muted small">Available Plots</div>
                                    <div className="h5">
                                        {systemConfig?.availablePlots?.blossom.available + systemConfig?.availablePlots?.university.available}
                                        <span className="text-muted small"> of {systemConfig?.availablePlots?.blossom.total + systemConfig?.availablePlots?.university.total}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="text-center py-2">
                                    <div className="text-muted small">Min. Initial Payment</div>
                                    <div className="h5">{systemConfig?.minimumPercentage}%</div>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {/* Filters */}
            <Card className="mb-4 shadow-sm">
                <Card.Header>
                    <LandAcquisitionFilters
                        filterStatus={filterStatus}
                        filterMemberType={filterMemberType}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        handleFilterChange={handleFilterChange}
                        handleMemberTypeChange={handleMemberTypeChange}
                        handleSearch={handleSearch}
                        resetFilters={resetFilters}
                    />
                </Card.Header>
            </Card>

            {/* Land Acquisition requests table */}
            <Card className="shadow-sm">
                <Card.Body className="p-0">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Loading land acquisition requests...</p>
                        </div>
                    ) : acquisitionRequests?.length === 0 ? (
                        <div className="text-center py-5">
                            <FileEarmark size={48} className="text-muted mb-3" />
                            <h5>No land acquisition requests found</h5>
                            <p className="text-muted">No requests match your current filters</p>
                        </div>
                    ) : (
                        <>
                            <Table responsive hover className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>ID/Reference</th>
                                        <th>Name</th>
                                        <th>Estate</th>
                                        <th className="text-right">Plots</th>
                                        <th className="text-right">Total Amount</th>
                                        <th className="text-center">Status</th>
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {acquisitionRequests.map((request) => (
                                        <tr key={request._id}>
                                            <td>{new Date(request.createdAt || request.requestDate).toLocaleDateString()}</td>
                                            <td>{memberTypeBadge(request.memberType || 'member')}</td>
                                            <td>{request.memberId || request._id.substring(0, 8)}</td>
                                            <td>{request.personalInfo?.fullName || "N/A"}</td>
                                            <td>
                                                <Badge variant="info" className="mr-1">
                                                    {request.acquisitionDetails?.estateChoice === 'blossom'
                                                        ? 'Blossom Estate'
                                                        : 'University Layout'}
                                                </Badge>
                                            </td>
                                            <td className="text-right">
                                                {request.acquisitionDetails?.numberOfPlots || 0}
                                            </td>
                                            <td className="text-right">
                                                {formatCurrency(request.acquisitionDetails?.totalAmount || 0)}
                                            </td>
                                            <td className="text-center">
                                                {statusBadgeElement(request.status)}
                                            </td>
                                            <td className="text-center">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => handleViewDetails(request)}
                                                >
                                                    View Details
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            <PaginationComponent
                                pagination={pagination}
                                currentPage={currentPage}
                                handlePageChange={handlePageChange}
                            />
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* System Configuration Modal */}
            <Modal
                show={showConfigModal}
                onHide={() => setShowConfigModal(false)}
                size="lg"
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>System Configuration</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {configError && <Alert variant="danger">{configError}</Alert>}
                    {configFormData && (
                        <Form>
                            <h6 className="mb-3">Pricing Configuration</h6>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Member Plot Price</Form.Label>
                                        <div className="input-group">
                                            <div className="input-group-prepend">
                                                <span className="input-group-text">₦</span>
                                            </div>
                                            <Form.Control
                                                type="number"
                                                name="plotPrice"
                                                value={configFormData.plotPrice}
                                                onChange={handleConfigInputChange}
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Non-Member Plot Price</Form.Label>
                                        <div className="input-group">
                                            <div className="input-group-prepend">
                                                <span className="input-group-text">₦</span>
                                            </div>
                                            <Form.Control
                                                type="number"
                                                name="nonMemberPlotPrice"
                                                value={configFormData.nonMemberPlotPrice}
                                                onChange={handleConfigInputChange}
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <h6 className="mb-3">Payment Terms</h6>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Minimum Initial Payment Percentage</Form.Label>
                                        <div className="input-group">
                                            <Form.Control
                                                type="number"
                                                name="minimumPercentage"
                                                value={configFormData.minimumPercentage}
                                                onChange={handleConfigInputChange}
                                            />
                                            <div className="input-group-append">
                                                <span className="input-group-text">%</span>
                                            </div>
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Maximum Installment Months</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="maximumInstallments"
                                            value={configFormData.maximumInstallments}
                                            onChange={handleConfigInputChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <h6 className="mb-3">Plot Availability</h6>
                            <Table responsive className="mb-4">
                                <thead className="bg-light">
                                    <tr>
                                        <th>Estate</th>
                                        <th className="text-center">Total Plots</th>
                                        <th className="text-center">Available Plots</th>
                                        <th className="text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Blossom Estate</td>
                                        <td className="text-center">
                                            <Form.Control
                                                type="number"
                                                name="availablePlots.blossom.total"
                                                value={configFormData.availablePlots.blossom.total}
                                                onChange={handleConfigInputChange}
                                                className="text-center"
                                                size="sm"
                                                style={{ width: '80px', margin: '0 auto' }}
                                            />
                                        </td>
                                        <td className="text-center">
                                            <Form.Control
                                                type="number"
                                                name="availablePlots.blossom.available"
                                                value={configFormData.availablePlots.blossom.available}
                                                onChange={handleConfigInputChange}
                                                className="text-center"
                                                size="sm"
                                                style={{ width: '80px', margin: '0 auto' }}
                                            />
                                        </td>
                                        <td className="text-center">
                                            <Form.Control
                                                as="select"
                                                value={configFormData.availablePlots.blossom.status}
                                                onChange={(e) => handleStatusChange('blossom', e.target.value)}
                                                size="sm"
                                                style={{ width: '100px', margin: '0 auto' }}
                                            >
                                                <option value="open">Open</option>
                                                <option value="closed">Closed</option>
                                            </Form.Control>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>University Layout</td>
                                        <td className="text-center">
                                            <Form.Control
                                                type="number"
                                                name="availablePlots.university.total"
                                                value={configFormData.availablePlots.university.total}
                                                onChange={handleConfigInputChange}
                                                className="text-center"
                                                size="sm"
                                                style={{ width: '80px', margin: '0 auto' }}
                                            />
                                        </td>
                                        <td className="text-center">
                                            <Form.Control
                                                type="number"
                                                name="availablePlots.university.available"
                                                value={configFormData.availablePlots.university.available}
                                                onChange={handleConfigInputChange}
                                                className="text-center"
                                                size="sm"
                                                style={{ width: '80px', margin: '0 auto' }}
                                            />
                                        </td>
                                        <td className="text-center">
                                            <Form.Control
                                                as="select"
                                                value={configFormData.availablePlots.university.status}
                                                onChange={(e) => handleStatusChange('university', e.target.value)}
                                                size="sm"
                                                style={{ width: '100px', margin: '0 auto' }}
                                            >
                                                <option value="open">Open</option>
                                                <option value="closed">Closed</option>
                                            </Form.Control>
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancel} disabled={isSavingConfig}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveConfig} disabled={isSavingConfig}>
                        {isSavingConfig ? <><Spinner as="span" animation="border" size="sm" /> Saving...</> : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Request detail modals */}
            {selectedRequest && (
                <>
                    <LandAcquisitionDetailsModal
                        show={showDetailsModal}
                        onHide={closeModals}
                        request={selectedRequest}
                        processingAction={processingAction}
                        handleUpdateStatus={handleUpdateStatus}
                        StatusBadge={StatusBadge}
                    />

                    <CommentsModal
                        show={showCommentsModal}
                        onHide={() => setShowCommentsModal(false)}
                        comments={comments}
                        setComments={setComments}
                        processingAction={processingAction}
                        onConfirm={() => handleUpdateStatus("rejected")}
                    />
                </>
            )}
        </ContentWrapper>
    );
}

export default LandAcquisitionManagement;